import * as SliderPrimitive from '@radix-ui/react-slider'
import { cn } from '@/utils'
import {
  FIGMA_TEXT_MUTED,
  PEZ_SLIDER_WIDTH,
} from '@/components/app/photos/photo-effects/constants/photo-effect-row-layout'
import { computeFill } from '../helpers/photo-effect-slider-fill'
import type { PhotoEffectSliderMode } from '../types/photo-effect-slider-mode'

const FIGMA_TRACK = '#F5F5F5'
const FIGMA_FILL = '#144087'
const FIGMA_THUMB_SHADOW = '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1)'

export type PhotoEffectSliderProps = {
  mode: PhotoEffectSliderMode
  value: number
  min: number
  max: number
  /** When false the track/thumb stay muted while dragging (WMPCM-9477 / Figma dev note). */
  active: boolean
  onValueChange: (value: number) => void
  /** WMPCM-9494 — visible row label id (preferred over aria-label). */
  ariaLabelledBy?: string
  ariaValueText?: string
  onSliderFocus?: () => void
  onSliderBlur?: () => void
  className?: string
}

/**
 * PEZ slider (WMPCM-9477) — Radix primitives with Figma track/fill, not a fork of ui/slider.
 * Symmetric: center-origin fill from 0. Percent: left-origin fill from min.
 */
export function PhotoEffectSlider({
  mode,
  value,
  min,
  max,
  active,
  onValueChange,
  ariaLabelledBy,
  ariaValueText,
  onSliderFocus,
  onSliderBlur,
  className,
}: PhotoEffectSliderProps) {
  const { leftPct, widthPct } = computeFill(mode, value, min, max)
  const showFill = active && widthPct > 0
  const thumbBorder = active ? FIGMA_FILL : FIGMA_TEXT_MUTED

  return (
    <SliderPrimitive.Root
      data-photo-effect-slider
      data-photo-effect-slider-mode={mode}
      data-photo-effect-slider-active={active ? 'true' : 'false'}
      className={cn(
        'relative flex w-full max-w-[110px] touch-none select-none items-center',
        className
      )}
      style={{ width: PEZ_SLIDER_WIDTH, height: 6 }}
      min={min}
      max={max}
      step={1}
      value={[value]}
      onValueChange={([next]) => {
        if (next != null) onValueChange(next)
      }}
    >
      <SliderPrimitive.Track
        className="relative h-[6px] w-full grow overflow-hidden rounded-full"
        style={{ backgroundColor: FIGMA_TRACK }}
      >
        {showFill ? (
          <div
            data-photo-effect-slider-fill
            className="absolute top-0 h-full rounded-full"
            style={{
              left: `${leftPct}%`,
              width: `${widthPct}%`,
              backgroundColor: FIGMA_FILL,
            }}
          />
        ) : null}
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        aria-labelledby={ariaLabelledBy}
        aria-valuetext={ariaValueText}
        onFocus={onSliderFocus}
        onBlur={onSliderBlur}
        className="block size-4 rounded-full border bg-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        style={{
          borderColor: thumbBorder,
          boxShadow: active ? FIGMA_THUMB_SHADOW : 'none',
        }}
      />
    </SliderPrimitive.Root>
  )
}
